<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { AuthenticationType, type BasicImage } from 'gpcore-grpc/gpcore/api/cloud/v1/image_pb';
import OrderSection from '../OrderSection.vue';
import PasswordMethod from './PasswordMethod.vue';
import SshKeysMethod from './SshKeysMethod.vue';

export interface Authentication {
  type: AuthenticationType;
  password?: string;
  sshKeys?: Array<string>;
}

export interface Props {
  modelValue: Authentication | null;
  currentImage: BasicImage;
  preTitle?: string;
  title?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{ (e: 'update:modelValue', value: Authentication) }>();

const selectedType = ref<AuthenticationType>(AuthenticationType.UNSPECIFIED);
const password = ref<string>('');
const sshKeys = ref<Array<string>>([]);

const updateModelValue = () => {
  emit('update:modelValue', {
    type: selectedType.value,
    password: password.value,
    sshKeys: sshKeys.value,
  });
};

const handleAuthTypeSelect = (authType: AuthenticationType) => {
  selectedType.value = authType;
};

const onKeysChange = (keys: Array<string>) => {
  if (keys.length > 0) {
    selectedType.value = AuthenticationType.SSH;
    password.value = '';
  }

  sshKeys.value = keys;
  updateModelValue();
};

const handlePasswordInput = (pw: string) => {
  password.value = pw;

  emit('update:modelValue', {
    type: selectedType.value,
    password: password.value,
    sshKeys: sshKeys.value,
  });

  updateModelValue();
};

const isDone = computed(
  () =>
    (selectedType.value === AuthenticationType.SSH && sshKeys.value.length > 0) ||
    (selectedType.value === AuthenticationType.PASSWORD &&
      password.value != null &&
      password.value.length > 0),
);

const preselectLoginMethod = () => {
  switch (props.currentImage.authenticationTypes[0]) {
    case AuthenticationType.SSH:
      selectedType.value = AuthenticationType.SSH;
      break;
    case AuthenticationType.PASSWORD:
      selectedType.value = AuthenticationType.PASSWORD;
      break;
    default:
      break;
  }
};
watch(
  () => props.currentImage,
  () => {
    preselectLoginMethod();
  },
);

watch(
  () => props.modelValue,
  () => {
    if (props.modelValue != null) {
      selectedType.value = props.modelValue.type;
      password.value = props.modelValue.password;
    } else {
      selectedType.value = AuthenticationType.UNSPECIFIED;
      password.value = '';
    }
  },
);

onMounted(() => {
  preselectLoginMethod();
});
</script>

<template>
  <OrderSection
    :title="title"
    :pre-title="preTitle"
    :is-done="(selectedType != null || selectedType !== AuthenticationType.UNSPECIFIED) && isDone"
    :is-invalid="selectedType != null && !isDone"
    section-key="login"
    v-playwright="'nodes-create-login'"
  >
    <p class="tw-mb-4">
      For more security, we recommend using
      <RouterLink :to="{ name: 'UserSSHKeys' }">SSH public keys</RouterLink> instead of passwords.
    </p>

    <div class="tw-mt-4 tw-grid tw-grid-cols-1 tw-gap-4 tw-gap-y-3 md:tw-grid-cols-2">
      <template v-if="currentImage.authenticationTypes.includes(AuthenticationType.SSH)">
        <SshKeysMethod
          class="tw-h-full"
          :is-active="selectedType === AuthenticationType.SSH"
          @keys-change="onKeysChange"
          @auth-type-select="handleAuthTypeSelect(AuthenticationType.SSH)"
        />
      </template>
      <template v-if="currentImage.authenticationTypes.includes(AuthenticationType.PASSWORD)">
        <PasswordMethod
          class="tw-h-full"
          :password="password"
          :is-active="selectedType === AuthenticationType.PASSWORD"
          :is-image-supporting-password="
            currentImage.authenticationTypes.includes(AuthenticationType.SSH)
          "
          @update:password="handlePasswordInput"
          @auth-type-select="handleAuthTypeSelect(AuthenticationType.PASSWORD)"
        />
      </template>
    </div>
  </OrderSection>
</template>

<style scoped></style>
